import { useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type {
  ProfileOption,
  Project,
  SalesTool,
  SalesToolFileMetadata,
  SalesToolKind,
} from "../types";
import {
  TEXT_ONLY_TOOL_KINDS,
  toggleTag,
  validateSalesToolForm,
} from "./salesToolFormRules";
import { SALES_TOOL_KINDS } from "./salesToolRelevance";
import { SALES_TOOLS_QUERY_KEY } from "./useSalesTools";

const FIELD_CLASS = "h-11 sm:h-9";
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

type TagGroupProps<T extends string | number> = {
  legend: string;
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
  allLabel: string;
};

const TagGroup = <T extends string | number>({
  legend,
  options,
  selected,
  onToggle,
  allLabel,
}: TagGroupProps<T>) => (
  <fieldset className="space-y-2">
    <legend className="text-sm font-medium">{legend}</legend>
    {selected.length === 0 ? (
      <p className="text-xs text-muted-foreground">{allLabel}</p>
    ) : null}
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {options.map((option) => (
        <label
          key={String(option.value)}
          className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0"
        >
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            onChange={() => onToggle(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  </fieldset>
);

/**
 * Almikia Epic EH (TASK-014): create / edit dialog of a sales tool, for direccion and lead_manager
 * (the library only mounts it for them). The row is saved first, then the optional file is
 * uploaded to the private bucket under a NEW path (the old object is kept). Empty tag groups
 * mean "Aplica a todos". There is no delete anywhere: tools are archived from the library.
 */
export const SalesToolForm = ({
  tool,
  projects,
  buyerTypes,
  onClose,
}: {
  tool: SalesTool | null;
  projects: Project[];
  buyerTypes: ProfileOption[];
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { dealStages } = useConfigurationContext();
  const { data: identity } = useGetIdentity();

  const [title, setTitle] = useState(tool?.title ?? "");
  const [kind, setKind] = useState<SalesToolKind | "">(tool?.kind ?? "");
  const [body, setBody] = useState(tool?.body ?? "");
  const [videoUrl, setVideoUrl] = useState(tool?.video_url ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [stageKeys, setStageKeys] = useState<string[]>(tool?.stage_keys ?? []);
  const [projectIds, setProjectIds] = useState<Identifier[]>(
    tool?.project_ids ?? [],
  );
  const [buyerTypeIds, setBuyerTypeIds] = useState<Identifier[]>(
    tool?.buyer_type_ids ?? [],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Once created, a later submit must update the row instead of inserting a duplicate.
  const savedId = useRef<Identifier | null>(tool?.id ?? null);

  const err = (field: string) =>
    errors[field]
      ? translate(`crm.salesTools.form.errors.${errors[field]}`)
      : null;

  const textOnly = kind !== "" && TEXT_ONLY_TOOL_KINDS.includes(kind);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validateSalesToolForm({
      title,
      kind,
      body,
      videoUrl,
      file,
      hasExistingFile: Boolean(tool?.storage_path),
    });
    setErrors(found);
    if (Object.keys(found).length > 0 || kind === "") return;

    setSaving(true);
    const data = {
      title: title.trim(),
      kind,
      body: body.trim() || null,
      video_url: videoUrl.trim() || null,
      stage_keys: stageKeys,
      project_ids: projectIds,
      buyer_type_ids: buyerTypeIds,
    };
    // Upload first: the object path does not depend on the row id, and a file-only
    // tool must be inserted with its file metadata to satisfy sales_tools_content_check.
    let fileMetadata: SalesToolFileMetadata | null = null;
    if (file) {
      try {
        fileMetadata = await dataProvider.uploadSalesToolObject(file);
      } catch {
        setSaving(false);
        setErrors({ file: "upload_failed" });
        return;
      }
    }
    try {
      if (savedId.current == null) {
        // RLS policy sales_tools_insert requires created_by = current sales id, and nothing
        // server-side defaults it, so the client must send it on create (never on update).
        const created = await dataProvider.create<SalesTool>("sales_tools", {
          data: { ...data, ...fileMetadata, created_by: identity?.id },
        });
        savedId.current = created.data.id;
      } else {
        await dataProvider.update<SalesTool>("sales_tools", {
          id: savedId.current,
          data: { ...data, ...fileMetadata },
          previousData: tool ?? { id: savedId.current },
        });
      }
    } catch {
      setSaving(false);
      notify("crm.salesTools.form.save_error", { type: "error" });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: [SALES_TOOLS_QUERY_KEY] });
    notify("crm.salesTools.form.saved", { type: "success" });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate(
              tool
                ? "crm.salesTools.form.edit_title"
                : "crm.salesTools.form.create_title",
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tool-form-title">
              {translate("crm.salesTools.form.title")}
            </Label>
            <Input
              id="tool-form-title"
              className={FIELD_CLASS}
              value={title}
              aria-invalid={errors.title ? true : undefined}
              onChange={(e) => setTitle(e.target.value)}
            />
            {err("title") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("title")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-form-kind">
              {translate("crm.salesTools.form.kind")}
            </Label>
            <select
              id="tool-form-kind"
              className={SELECT_CLASS}
              value={kind}
              aria-invalid={errors.kind ? true : undefined}
              onChange={(e) => {
                const next = e.target.value as SalesToolKind | "";
                setKind(next);
                // A text-only kind hides the file input: drop any stale selection.
                if (next !== "" && TEXT_ONLY_TOOL_KINDS.includes(next)) {
                  setFile(null);
                }
              }}
            >
              <option value="">
                {translate("crm.salesTools.form.kind_placeholder")}
              </option>
              {SALES_TOOL_KINDS.map((value) => (
                <option key={value} value={value}>
                  {translate(`crm.salesTools.kinds.${value}`)}
                </option>
              ))}
            </select>
            {err("kind") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("kind")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-form-body">
              {translate("crm.salesTools.form.body")}
            </Label>
            <Textarea
              id="tool-form-body"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tool-form-video">
              {translate("crm.salesTools.form.video_url")}
            </Label>
            <Input
              id="tool-form-video"
              type="url"
              inputMode="url"
              className={FIELD_CLASS}
              value={videoUrl}
              aria-invalid={errors.video_url ? true : undefined}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            {err("video_url") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("video_url")}
              </p>
            ) : null}
          </div>
          {textOnly ? null : (
            <div className="space-y-2">
              <Label htmlFor="tool-form-file">
                {translate("crm.salesTools.form.file")}
              </Label>
              {tool?.storage_path ? (
                <p className="text-xs text-muted-foreground">
                  {translate("crm.salesTools.form.file_current", {
                    name: tool.file_name ?? "",
                  })}
                </p>
              ) : null}
              <Input
                id="tool-form-file"
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                className={FIELD_CLASS}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                {translate("crm.salesTools.form.file_hint")}
              </p>
              {err("file") ? (
                <p role="alert" className="text-sm text-destructive">
                  {err("file")}
                </p>
              ) : null}
            </div>
          )}
          {err("content") ? (
            <p role="alert" className="text-sm text-destructive">
              {err("content")}
            </p>
          ) : null}
          {errors.file && textOnly ? (
            <p role="alert" className="text-sm text-destructive">
              {err("file")}
            </p>
          ) : null}
          <TagGroup
            legend={translate("crm.salesTools.form.stages")}
            options={dealStages.map((stage) => ({
              value: stage.value,
              label: stage.label,
            }))}
            selected={stageKeys}
            onToggle={(value) => setStageKeys(toggleTag(stageKeys, value))}
            allLabel={translate("crm.salesTools.form.applies_to_all")}
          />
          <TagGroup
            legend={translate("crm.salesTools.form.projects")}
            options={projects
              .filter((project) => !project.archived)
              .map((project) => ({ value: project.id, label: project.name }))}
            selected={projectIds}
            onToggle={(value) => setProjectIds(toggleTag(projectIds, value))}
            allLabel={translate("crm.salesTools.form.applies_to_all")}
          />
          <TagGroup
            legend={translate("crm.salesTools.form.buyer_types")}
            options={buyerTypes
              .filter((option) => !option.archived_at)
              .map((option) => ({ value: option.id, label: option.label }))}
            selected={buyerTypeIds}
            onToggle={(value) =>
              setBuyerTypeIds(toggleTag(buyerTypeIds, value))
            }
            allLabel={translate("crm.salesTools.form.applies_to_all")}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={onClose}
            >
              {translate("crm.salesTools.form.cancel")}
            </Button>
            <Button
              type="submit"
              className="h-11 sm:h-9"
              disabled={saving || identity == null}
            >
              {translate("crm.salesTools.form.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
